import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import CustomDropdown from './CustomDropdown';
import { useNavigate } from 'react-router-dom';

const getUniqueYears = (dates) => {
    if (!dates || dates.length === 0) return [];
    const years = new Set(dates.map(date => new Date(date).getFullYear()));
    return Array.from(years).sort((a, b) => b - a);
};

const getMonthName = (monthIndex) => {
    return new Date(2000, monthIndex).toLocaleString('default', { month: 'long' });
};

const getDaysInMonth = (year, monthIndex) => {
    return new Date(year, monthIndex + 1, 0).getDate();
};

const formatDate = (year, monthIndex, day) => {
    const month = (monthIndex + 1).toString().padStart(2, '0');
    const d = day.toString().padStart(2, '0');
    return `${year}-${month}-${d}`;
};

const SingleDateCalendar = ({ onDateSelect, initialSelectedDate }) => {
    const navigate = useNavigate();

    const datesFromStorage = localStorage.getItem('calendarDates');
    const availableDates = datesFromStorage ? JSON.parse(datesFromStorage) : [];

    const availableDatesSet = useMemo(() => new Set(availableDates), [availableDates]);
    const allAvailableYears = useMemo(() => getUniqueYears(availableDates), [availableDates]);

    // Normalize initialSelectedDate into yyyy-mm-dd
    const normalizeDate = (dateStr) => {
        if (!dateStr) return null;
        const parts = dateStr.split("-");
        if (parts.length === 3) {
            if (parts[0].length === 4) {
                // already yyyy-mm-dd
                return dateStr;
            } else {
                // dd-mm-yyyy → yyyy-mm-dd
                const [day, month, year] = parts;
                return `${year}-${month}-${day}`;
            }
        }
        return null;
    };

    const normalizedInitialDate = normalizeDate(initialSelectedDate);

    const initialViewDate = normalizedInitialDate
        ? new Date(normalizedInitialDate)
        : (availableDates[0] ? new Date(availableDates[0]) : new Date());

    const [currentDate, setCurrentDate] = useState(initialViewDate);
    const [stagedSelectedDate, setStagedSelectedDate] = useState(normalizedInitialDate);

    useEffect(() => {
        const normalized = normalizeDate(initialSelectedDate);
        setStagedSelectedDate(normalized);
        if (normalized) {
            setCurrentDate(new Date(normalized));
        }
    }, [initialSelectedDate]);

    const monthsForYear = useCallback((year, allDates) => {
        const months = new Set();
        allDates.forEach(dateStr => {
            const date = new Date(dateStr);
            if (date.getFullYear() === year) {
                months.add(date.getMonth());
            }
        });
        return Array.from(months).sort((a, b) => a - b);
    }, []);

    const availableMonthsForCurrentYear = useMemo(() => {
        return monthsForYear(currentDate.getFullYear(), availableDates);
    }, [currentDate, availableDates, monthsForYear]);

    useEffect(() => {
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth();

        if (!availableMonthsForCurrentYear.includes(currentMonth) && availableMonthsForCurrentYear.length > 0) {
            setCurrentDate(new Date(currentYear, availableMonthsForCurrentYear[0], 1));
        } else if (availableMonthsForCurrentYear.length === 0 && allAvailableYears.length > 0) {
            const firstOverallDate = new Date(Array.from(availableDates).sort()[0]);
            setCurrentDate(new Date(firstOverallDate.getFullYear(), firstOverallDate.getMonth(), 1));
        } else if (availableDates.length === 0) {
            setCurrentDate(new Date());
        }
    }, [currentDate.getFullYear(), currentDate.getMonth(), availableDates, allAvailableYears, availableMonthsForCurrentYear]);

    const handleDayClick = (day, monthIndex, year) => {
        const formattedDate = formatDate(year, monthIndex, day);
        if (!availableDatesSet.has(formattedDate)) return;

        if (formattedDate === stagedSelectedDate) {
            setStagedSelectedDate(null);
        } else {
            setStagedSelectedDate(formattedDate);
        }
    };

    const handlePrev = () => {
        setCurrentDate(prevDate => new Date(prevDate.getFullYear(), prevDate.getMonth() - 1, 1));
    };

    const handleNext = () => {
        setCurrentDate(prevDate => new Date(prevDate.getFullYear(), prevDate.getMonth() + 1, 1));
    };

    const handleYearChange = (newYearValue) => {
        const newYear = parseInt(newYearValue, 10);
        const currentMonth = currentDate.getMonth();
        let targetMonth = currentMonth;
        const monthsForSelectedYear = monthsForYear(newYear, availableDates);

        if (!monthsForSelectedYear.includes(currentMonth) && monthsForSelectedYear.length > 0) {
            targetMonth = monthsForSelectedYear[0];
        } else if (monthsForSelectedYear.length === 0 && allAvailableYears.length > 0) {
            const firstAvailableYear = allAvailableYears[0];
            const monthsForFirstAvailableYear = monthsForYear(firstAvailableYear, availableDates);
            targetMonth = monthsForFirstAvailableYear.length > 0 ? monthsForFirstAvailableYear[0] : 0;
            setCurrentDate(new Date(firstAvailableYear, targetMonth, 1));
            return;
        } else if (monthsForSelectedYear.length === 0 && allAvailableYears.length === 0) {
            setCurrentDate(new Date(newYear, currentMonth, 1));
            return;
        }

        setCurrentDate(new Date(newYear, targetMonth, 1));
    };

    const handleMonthChange = (newMonthValue) => {
        const newMonth = parseInt(newMonthValue, 10);
        setCurrentDate(new Date(currentDate.getFullYear(), newMonth, 1));
    };

    const handleApply = () => {
        if (stagedSelectedDate) {
            onDateSelect(stagedSelectedDate);
        }
    };

    const handleClearSelection = () => {
        setStagedSelectedDate(null);
        onDateSelect(null);
        navigate(`/`);
    };

    const yearOptions = allAvailableYears.map(year => ({
        label: String(year),
        value: year
    }));

    const monthOptions = availableMonthsForCurrentYear.map(monthIndex => ({
        label: getMonthName(monthIndex),
        value: monthIndex
    }));

    const renderMonthGrid = (date) => {
        const year = date.getFullYear();
        const monthIndex = date.getMonth();
        const daysInMonth = getDaysInMonth(year, monthIndex);
        const firstDayOfMonth = new Date(year, monthIndex, 1).getDay();
        const daysArray = Array.from({ length: daysInMonth }, (_, i) => i + 1);

        return (
            <div className="flex-1 left-0">
                <div className="grid grid-cols-7 gap-1 text-center font-[Archivo]">
                    {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(day => (
                        <div key={day} className="text-xs font-medium text-gray-400 font-[Archivo]">{day}</div>
                    ))}
                </div>
                <div className="grid grid-cols-7 gap-1 mt-1 font-[Archivo]">
                    {Array.from({ length: firstDayOfMonth }).map((_, i) => <div key={`empty-${i}`} className="h-8"></div>)}
                    {daysArray.map(day => {
                        const formattedDate = formatDate(year, monthIndex, day);
                        const isAvailable = availableDatesSet.has(formattedDate);

                        // ✅ highlight staged OR initialSelectedDate
                        const isSelected =
                            formattedDate === stagedSelectedDate ||
                            (!stagedSelectedDate && normalizedInitialDate && formattedDate === normalizedInitialDate);

                        const isDisabled = !isAvailable;

                        return (
                            <div
                                key={day}
                                onClick={() => handleDayClick(day, monthIndex, year)}
                                className={`
                                    relative flex items-center justify-center h-8 w-8 mx-auto text-sm rounded-full transition-colors
                                    ${isDisabled ? 'text-gray-300 cursor-not-allowed' : 'cursor-pointer'}
                                    ${isSelected ? 'bg-blue-600 text-white font-bold' : ''}
                                    ${!isDisabled && !isSelected ? 'hover:bg-gray-100' : ''}
                                `}
                            >
                                {day}
                                {isAvailable && !isSelected && (
                                    <div className="absolute bottom-1 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full bg-green-500"></div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <div className="flex flex-col items-center p-3 mx-auto w-full max-w-xs bg-white rounded-lg shadow-md border font-[Archivo]">
            {/* Header with navigation + dropdowns */}
            <div className="flex justify-between items-center w-full mb-3">
                <button onClick={handlePrev} className="p-1.5 mr-2 rounded-full hover:bg-gray-100">
                    <FaChevronLeft className="h-4 w-4 text-gray-600" />
                </button>
                <div className="flex flex-row items-center gap-1">
                    <CustomDropdown
                        options={yearOptions}
                        value={currentDate.getFullYear()}
                        onChange={handleYearChange}
                        placeholder="Year"
                        buttonClass="min-w-[80px] text-sm"
                    />
                    <CustomDropdown
                        options={monthOptions}
                        value={currentDate.getMonth()}
                        onChange={handleMonthChange}
                        placeholder="Month"
                        buttonClass="min-w-[100px] text-sm"
                    />
                </div>
                <button onClick={handleNext} className="p-1.5 ml-2 rounded-full hover:bg-gray-100">
                    <FaChevronRight className="h-4 w-4 text-gray-600" />
                </button>
            </div>

            {/* Calendar grid */}
            <div className="w-full flex justify-center px-1">
                {renderMonthGrid(currentDate)}
            </div>

            {/* Footer buttons */}
            <div className="flex justify-end gap-2 mt-4 pt-2 border-t w-full">
                <button
                    onClick={handleClearSelection}
                    disabled={!stagedSelectedDate}
                    className={`px-3 py-1.5 text-xs rounded-md font-[Archivo] 
                        ${stagedSelectedDate 
                            ? 'text-gray-700 bg-gray-200 hover:bg-gray-300' 
                            : 'text-gray-400 bg-gray-100 cursor-not-allowed'}`}
                >
                    Clear
                </button>
                <button
                    onClick={handleApply}
                    disabled={!stagedSelectedDate}
                    className={`px-3 py-1.5 text-xs rounded-md font-[Archivo] 
                        ${stagedSelectedDate
                            ? 'text-white bg-[#2573EB] hover:bg-[#1a5fcc]'
                            : 'text-gray-400 bg-gray-100 cursor-not-allowed'}`}
                >
                    Apply
                </button>
            </div>
        </div>
    );
};

export default SingleDateCalendar;
